import {
  QUOTE_DOCUMENT_RECOVERY_PREFIX,
  QUOTE_DOCUMENT_RECOVERY_TTL_MS,
} from '@/constant/quoteDocuments';
import { CommercialNarrativeDraft } from '@/utils/commercialNarrativeDraft';
import storage from '@/utils/storage';

export type QuoteDocumentRecoveryRecord = {
  companyId: string;
  userId: number;
  documentId: string;
  baseRevision: number;
  savedAt: number;
  draft: CommercialNarrativeDraft;
};

const recoveryKey = (
  companyId: string,
  userId: number,
  documentId: string,
  baseRevision: number
): string =>
  `${QUOTE_DOCUMENT_RECOVERY_PREFIX}${companyId}:${userId}:${documentId}:${baseRevision}`;

export const writeQuoteDocumentRecovery = (
  record: QuoteDocumentRecoveryRecord
): void => {
  storage.setItem(
    recoveryKey(
      record.companyId,
      record.userId,
      record.documentId,
      record.baseRevision
    ),
    JSON.stringify(record)
  );
};

export const readQuoteDocumentRecovery = (
  companyId: string,
  userId: number,
  documentId: string,
  baseRevision: number
): QuoteDocumentRecoveryRecord | null => {
  const raw = storage.getItem(
    recoveryKey(companyId, userId, documentId, baseRevision)
  );
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as QuoteDocumentRecoveryRecord;
    if (
      !parsed ||
      parsed.companyId !== companyId ||
      parsed.userId !== userId ||
      parsed.documentId !== documentId ||
      parsed.baseRevision !== baseRevision ||
      typeof parsed.savedAt !== 'number' ||
      !parsed.draft
    ) {
      return null;
    }
    if (Date.now() - parsed.savedAt > QUOTE_DOCUMENT_RECOVERY_TTL_MS) {
      storage.removeItem(
        recoveryKey(companyId, userId, documentId, baseRevision)
      );
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/** Latest non-expired recovery for a document, regardless of base revision. */
export const findQuoteDocumentRecovery = (
  companyId: string,
  userId: number,
  documentId: string
): QuoteDocumentRecoveryRecord | null => {
  const prefix = `${QUOTE_DOCUMENT_RECOVERY_PREFIX}${companyId}:${userId}:${documentId}:`;
  try {
    let latest: QuoteDocumentRecoveryRecord | null = null;
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(prefix)) continue;
      const raw = storage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as QuoteDocumentRecoveryRecord;
        if (
          !parsed ||
          parsed.companyId !== companyId ||
          parsed.userId !== userId ||
          parsed.documentId !== documentId ||
          typeof parsed.savedAt !== 'number' ||
          !parsed.draft
        ) {
          continue;
        }
        if (Date.now() - parsed.savedAt > QUOTE_DOCUMENT_RECOVERY_TTL_MS) {
          storage.removeItem(key);
          continue;
        }
        if (!latest || parsed.savedAt > latest.savedAt) {
          latest = parsed;
        }
      } catch {
        // ignore malformed recovery rows
      }
    }
    return latest;
  } catch {
    return null;
  }
};

export const clearQuoteDocumentRecovery = (
  companyId: string,
  userId: number,
  documentId: string,
  baseRevision?: number
): void => {
  if (baseRevision != null) {
    storage.removeItem(
      recoveryKey(companyId, userId, documentId, baseRevision)
    );
    return;
  }

  const prefix = `${QUOTE_DOCUMENT_RECOVERY_PREFIX}${companyId}:${userId}:${documentId}:`;
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach(key => storage.removeItem(key));
  } catch {
    // ignore storage enumeration failures
  }
};

export const clearAllQuoteDocumentRecovery = (): void => {
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(QUOTE_DOCUMENT_RECOVERY_PREFIX)) keys.push(key);
    }
    keys.forEach(key => storage.removeItem(key));
  } catch {
    // ignore storage enumeration failures
  }
};
